import React, { useEffect } from 'react';

import { PageProps, graphql } from 'gatsby';
// @ts-expect-error - no type delcaration exists for this package
import { Generic, JSONLD } from 'react-structured-data';
import { Col, Row } from 'reactstrap';
import styled from 'styled-components';

import ArticleContent from 'components/Article/ArticleContent';
import ArticleSideContent from 'components/Article/ArticleSideContent/ArticleSideContent';
import ContentContainer from 'components/ContentContainer/ContentContainer';
import { ContentstackBrowserPlugin } from 'components/ContentstackBrowserPlugin/ContentstackBrowserPlugin';
import Breadcrumbs from 'components/Layout/Header/components/Breadcrumbs/Breadcrumbs';
import Layout from 'components/Layout/Layout';
import PriceTicker from 'components/PriceTicker';
import SEO from 'components/SEO';

import { useContentstackData } from 'hooks/useContentstackData';
import useScroll from 'hooks/useScroll';

import { BlogPostI } from 'interfaces/content-stack/blogPost';

import { htmlToPlainText } from 'utils/formatting';
import { dataLayerPush, getRawText } from 'utils/functions';

interface ArticlePageQueryProps {
  contentstackArticles: BlogPostI;
  site: { siteMetadata: { siteUrl: string } };
}

type ArticleProps = PageProps<ArticlePageQueryProps, { allArticles: string }>;

const ArticleContentWrapper = styled.main<{ $scrollUp?: boolean; $scrollY: number }>`
  background-color: ${(props) => props.theme.colors.whiteSmoke};
  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    background-color: ${(props) => props.theme.colors.white};
  }

  @media (min-width: ${(props) => props.theme.breakpoints.lg}) {
    transition: all 0.5s;
    transform: ${(props) => (props.$scrollUp && scrollY < 100 ? 'translateY(-2.3rem)' : 'none')};
  }
`;

const article: React.FC<ArticleProps> = ({ data, pageContext }) => {
  const siteUrl = data.site.siteMetadata.siteUrl;
  const { contentstackArticles: initPageData } = data;
  const page = useContentstackData(initPageData);
  const { scrollDirection, scrollY } = useScroll();
  const {
    title,
    seo,
    breadcrumb,
    url,
    pageType,
    metadata,
    id,
    postDate,
    updatedAt,
    toggleTicker,
    bodyContent,
    uid,
    locale,
    typename,
  } = page;
  const typeCategory = pageType?.[0]?.title;
  const categories = [...new Set(metadata?.categories?.map((category) => category.title))];
  const subcategory = categories?.length >= 1 ? categories[0] : undefined;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      dataLayerPush('page_data_loaded', 'event', {
        author_name: metadata?.articleAuthor?.[0]?.title,
        content_category: 'News Article',
        content_id: id,
        content_language: 'en',
        content_publication_date: postDate,
        content_subcategory: subcategory,
        content_title: title,
        content_volume: htmlToPlainText(bodyContent).trim().split(/\s+/).length,
        language: 'en_US',
        page_category: 'News Article',
        page_name: title,
        page_tags: categories,
        page_type: typeCategory,
      });
    }
  }, []);

  return (
    <Layout>
      <SEO
        title={title}
        seo={seo}
        thumbnail={page.metadata.thumbnailImage?.url}
        thumbnailAlt={page.metadata.thumbnailAltText}
      />
      <ContentstackBrowserPlugin attributes={{ uid, locale, typename }} />
      {/* SEO Structured Data - News Article */}
      <JSONLD dangerouslyExposeHtml>
        <Generic
          type='article'
          jsonldtype='NewsArticle'
          schema={{
            headline: title,
            datePublished: postDate,
            dateModified: updatedAt,
            description: getRawText(metadata.excerpt),
            author: metadata?.articleAuthor?.map((author) => ({
              '@type': 'Person',
              name: author.title,
              jobTitle: author.authorRole,
              url: `${siteUrl}/news${author.url}`,
            })),
          }}
        >
          <Generic
            type='image'
            jsonldtype='ImageObject'
            schema={{
              url: `${metadata.thumbnailImage?.url}?width=1200`,
            }}
          />
          <Generic
            type='publisher'
            jsonldtype='Organization'
            schema={{
              name: 'tastyfx',
            }}
          >
            <Generic
              type='logo'
              jsonldtype='ImageObject'
              schema={{
                url: 'https://images.contentstack.io/v3/assets/bltc23b87e0fef43b66/blt2f3fd8b9334783d3/669ffb0985d50598433fa3f3/tfxlogo.jpg',
              }}
            />
          </Generic>
        </Generic>
      </JSONLD>
      <ArticleContentWrapper $scrollUp={scrollDirection === 'up'} $scrollY={scrollY}>
        <Breadcrumbs breadcrumbs={breadcrumb} page={{ title, url }} scrollDirection={scrollDirection} />
        {!toggleTicker && <PriceTicker />}
        <ContentContainer>
          <Row>
            <Col lg={8}>
              <ArticleContent data={page} siteUrl={siteUrl} />
            </Col>
            <Col lg={4}>
              <ArticleSideContent data={page} siteUrl={siteUrl} allArticles={pageContext.allArticles} />
            </Col>
          </Row>
        </ContentContainer>
      </ArticleContentWrapper>
    </Layout>
  );
};

export const query = graphql`
  query ($id: String) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    contentstackArticles(id: { eq: $id }) {
      cslp__meta
      id
      url
      title
      tags
      uid
      locale
      typename: __typename
      pageType: page_type {
        title
      }
      toggleTicker: toggle_ticker
      breadcrumb {
        title
        url
      }
      metadata {
        excerpt
        articleAuthor: article_author {
          title
          url
          breadcrumb {
            title
            url
          }
          authorLocation: author_location
          authorRole: author_role
          authorThumbnail: author_thumbnail {
            url
          }
        }
        articleReviewer: article_reviewer {
          title
          url
          breadcrumb {
            title
            url
          }
          authorLocation: author_location
          authorRole: author_role
        }
        categories {
          title
          url
        }
        thumbnailImage: thumbnail_image {
          url
        }
        thumbnailImageCaption: thumbnail_image_caption
        thumbnailAltText: thumbnail_alt_text
        ...ArticleDisclaimerFragment
      }
      postDate: post_date
      updatedAt: updated_at
      sidebarCta: sidebar_cta {
        description
        header
        image {
          url
        }
        imageAltText: image_alt_text
        link {
          ... on Contentstack_brochure_page {
            id
            url
            title
          }
          ... on Contentstack_landing_page {
            id
            url
            title
          }
          ... on Contentstack_utility_page {
            id
            url
            title
          }
          ... on Contentstack_link_url {
            id
            title
            linkUrl: link_url {
              href
            }
          }
        }
      }
      bodyContent: body_content
      pageBody: page_body {
        basicTextBlock: basic_text_block {
          body
          header
        }
        contentCards: content_cards {
          ...ArticleContentCardsFragment
        }
      }
      seo {
        ...ArticleSeoFragment
      }
    }
  }
`;

export default React.memo(article);
